import * as React from 'react';

import {Card} from 'shells/card/card';
import {TimelineComments} from 'shells/timeline/entry/comments/timeline-comments';
import type {Props as TimelineCommentsProps} from 'shells/timeline/entry/comments/timeline-comments';
import {TimelineEntryFooter} from 'shells/timeline/entry/email-engagement-reaction-bar/timeline-entry-footer';
import type {Props as TimelineEntryFooterProps} from 'shells/timeline/entry/email-engagement-reaction-bar/timeline-entry-footer';
import {
    TimelineEntryToolbar,
    type Props as TimelineEntryToolbarProps,
} from 'shells/timeline/entry/toolbar/timeline-entry-toolbar';
import type {Reaction, Reactions} from 'shells/timeline/reaction-bar/types';

import type {ReactionType} from 'nutshell-graphql-types';

import {TimelineEntryContent} from './timeline-entry-content';
import type {Props as TimelineEntryContentProps} from './timeline-entry-content';

import styles from './timeline-entry.module.css';

type Props = {
    currentUserId?: string;
    changeLogId: string;
} & TimelineEntryContentProps & // Content props // Toolbar props
    TimelineEntryToolbarProps & // Timeline footer bar props
    TimelineEntryFooterProps & // Timeline comment props
    TimelineCommentsProps & {
        onToggleReaction?: (
            changeLogId: string,
            reactionType: ReactionType,
            filteredReactions: Reactions,
            parentChangeLogId?: string
        ) => any;
        filteredEntryReactions?: Reactions;
        // Email props
        customBorderProps?: {
            hasDottedBorder?: boolean;
            borderColor?: string;
        };
        disableComments?: boolean;
        disableReactions?: boolean;
        leftLineColor: string;
    };

export function TimelineEntryCard(props: Props) {
    const {
        changeLogId: entryChangeLogId,
        subject,
        subjectTitle,
        content,
        truncatedContent,
        status,
        filteredEntryReactions,
        comments,
        participants,
        relatedLeads,
        metadata,
        currentUserId,
        onToggleReaction,
        onAddComment,
        onDeleteComment,
        onClickOpen,
        customBorderProps,
        emailEngagement,
        threadData,
        disableComments,
        disableReactions,
        actionMenu,
        additionalToolbarOptions,
        leftLineColor,
        isExpanded,
        externalMeetingTranscriptSummary,
        hasActivityTranscription,
        isDashboard,
    } = props;

    const [shouldShowToolbar, setShouldShowToolbar] = React.useState<boolean>(false);
    const [isAddingComment, setIsAddingComment] = React.useState<boolean>(false);

    const onToggleEntryReaction =
        onToggleReaction && filteredEntryReactions
            ? (reactionType: ReactionType) => {
                  onToggleReaction(entryChangeLogId, reactionType, filteredEntryReactions);
              }
            : undefined;
    const onToggleEntryReactionForFooter =
        onToggleReaction && filteredEntryReactions
            ? (reaction: Reaction) => {
                  onToggleReaction(
                      entryChangeLogId,
                      reaction as ReactionType,
                      filteredEntryReactions
                  );
              }
            : undefined;
    const onToggleCommentReaction = onToggleReaction
        ? (changeLogId: string, reactionType: ReactionType, commentReactions: Reactions) =>
              onToggleReaction(changeLogId, reactionType, commentReactions, entryChangeLogId)
        : undefined;

    return (
        <div
            onMouseEnter={() => setShouldShowToolbar(true)}
            onMouseLeave={() => setShouldShowToolbar(false)}
            className={styles['timeline-entry-content']}
        >
            <Card
                hasBorder={true}
                hasShadow={true}
                {...customBorderProps}
                leftLineColor={leftLineColor}
            >
                <TimelineEntryContent
                    changeLogEntryId={entryChangeLogId}
                    content={content}
                    truncatedContent={truncatedContent}
                    subject={subject}
                    subjectTitle={subjectTitle}
                    status={status}
                    participants={participants}
                    relatedLeads={relatedLeads}
                    metadata={metadata}
                    onClickOpen={onClickOpen}
                    isExpanded={isExpanded}
                    externalMeetingTranscriptSummary={externalMeetingTranscriptSummary}
                    isDashboard={isDashboard}
                />
                <TimelineEntryFooter
                    disableReactions={disableReactions}
                    reactions={filteredEntryReactions}
                    currentUserId={currentUserId}
                    onToggleReaction={onToggleEntryReactionForFooter}
                    emailEngagement={emailEngagement}
                    threadData={threadData}
                    hasActivityTranscription={hasActivityTranscription}
                />
                <TimelineComments
                    comments={comments}
                    onToggleReaction={onToggleCommentReaction}
                    currentUserId={currentUserId}
                    onAddComment={onAddComment}
                    onDeleteComment={onDeleteComment}
                    isAddingComment={isAddingComment}
                    cancelAddingComment={() => setIsAddingComment(false)}
                />
            </Card>
            {shouldShowToolbar ? (
                <div className={styles['toolbar-container']}>
                    <TimelineEntryToolbar
                        onToggleReaction={onToggleEntryReaction}
                        handleComment={onAddComment ? () => setIsAddingComment(true) : undefined}
                        onClickOpen={onClickOpen}
                        disableComments={disableComments}
                        actionMenu={actionMenu}
                        additionalToolbarOptions={additionalToolbarOptions}
                        disableReactions={disableReactions}
                    />
                </div>
            ) : undefined}
        </div>
    );
}
